<template>
  <div>
    <h2>Step 1: Manuscript Details</h2>

    <v-card max-width="720px" class="mt-5">
      <v-card-text>
        <v-form
          class="mr-2 mt-4"
          v-model="valid"
          @submit.prevent="updateManuscript"
        >
          <!-- <h1
            class="mb-10 ml-8 green--text text--darken-2"
            style="line-height: 1.2"
          >
            {{ manuscript.title }}
          </h1> -->

          <v-text-field
            outlined
            label="Manuscript title *"
            prepend-icon="mdi-format-title"
            :rules="rules.required"
            v-model="manuscript.title"
          ></v-text-field>

          <v-select
            outlined
            :items="manuscriptTypes"
            label="Manuscript type *"
            prepend-icon="mdi-sticker-plus-outline"
            v-model="manuscript.type"
          ></v-select>

          <!-- <v-textarea
            autocomplete="email"
            outlined
            label="Abstract *"
            prepend-icon="mdi-sticker-text-outline"
            v-model="manuscript.abstract"
            hint="Maximum of 1600 characters"
            count
            counter="1600"
            :rules="rules.abstract"
          ></v-textarea> -->

          <div class="ml-8">
            <label for="abstract">Abstract *</label>
            <vue-pell-editor
              :placeholder="editorPlaceholder"
              :actions="editorOptions"
              :style-with-css="false"
              :classes="editorClasses"
              default-paragraph-separator="p"
              v-model="manuscript.abstract"
              class="mb-2 mt-1"
              id="abstract"
            />
          </div>

          <!-- Todo: maybe fetch the keywords that have been used like positions in eVoting -->
          <!-- :items="items" -->
          <v-combobox
            outlined
            label="Keywords *"
            prepend-icon="mdi-label-outline"
            v-model="manuscript.keywords"
            multiple
            chips
            hint="Type a keyword and hit enter. You may enter up to 5 keywords. "
          ></v-combobox>

          <div class="my-3 ml-8">
            <p>* mark required fields.</p>
            <v-btn
              large
              type="submit"
              color="green lighten-3"
              :disabled="!valid || loading"
              :loading="loading"
            >
              <v-icon left>mdi-content-save-check</v-icon>
              Update
            </v-btn>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions
        class="
          lime
          lighten-4
          text-center
          justify-center
          green--text
          text--darken-3
        "
        v-if="manuscript.special"
      >
        <h5 class="my-1 text-uppercase text--center">
          {{ manuscript.special.title }}
        </h5>
      </v-card-actions>
    </v-card>

    <h2 class="mt-8">Step 2: Manuscript Authors</h2>

    <v-card max-width="720px" class="mt-5">
      <v-card-text>
        <ManuscriptAuthors
          :manuscript="manuscriptId"
          :authors="manuscript.authors"
          @updated="fetchManuscript"
        />
      </v-card-text>
    </v-card>

    <h2 class="mt-8">Step 3: File Uploads</h2>

    <v-card max-width="720px" class="mt-5">
      <v-card-text>
        <ManuscriptFiles
          :manuscript="manuscriptId"
          :files="manuscript.files"
          :active="manuscript.authors.length > 0"
          @updated="fetchManuscript"
        />
      </v-card-text>
    </v-card>

    <h2 class="mt-8">Manuscript Processing Charge</h2>
    <v-card max-width="720px" class="mt-5 mb-8">
      <v-card-text>
        Accepted papers will require a processing fee of
        <strong
          v-if="selectedSpecialEdition && selectedSpecialEdition.apcLocal"
        >
          ₦{{ selectedSpecialEdition.apcLocal }}
        </strong>
        <strong v-else>${{ apc.apcInternational }} or its equivalent</strong>.
        For more information on this send an email to
        <a href="mailto:apc@iceesr.org.ng">apc@iceesr.org.ng</a>
      </v-card-text>
    </v-card>

    <p v-if="!canSubmit" class="orange--text text-darken-2">
      Please provide authors and upload files to be able to complete submission
    </p>

    <v-btn
      x-large
      color="green"
      :loading="submitting"
      :disabled="!canSubmit || submitting"
      class="mr-2 mb-2 white--text"
      @click="submitManuscript"
    >
      Submit Manuscript for Review
    </v-btn>
  </div>
</template>


<script>
import dedent from "dedent";
import { editorOptions } from "../../tools/wysiwyg";
import ManuscriptFiles from "./Includes/ManuscriptFiles";
import ManuscriptAuthors from "./Includes/ManuscriptAuthors";
import { required, attachment, abstract } from "../../tools/rules";
import { manuscriptTypes } from "../../tools/manuscript";

export default {
  components: { ManuscriptFiles, ManuscriptAuthors },

  props: ["manuscriptId"],

  data: () => ({
    valid: false,
    showPassword: false,
    rules: {
      required,
      attachment,
      abstract,
    },

    manuscript: {
      _id: null,
      title: null,
      type: "ORIGINAL RESEARCH",
      description: null,
      abstract: null,
      keywords: null,
      edition: null,
      authors: [],
      files: [],
    },

    loading: false,
    submitting: false,

    forSpecialEdition: false,
    specialEditions: [],

    manuscriptTypes,
    abstractOutput: null,

    editorOptions,
    editorPlaceholder: "Paste or type here..",
    editorClasses: {
      actionbar: "pell-actionbar",
      button: "pell-button",
      content: "pell-content",
      selected: "pell-button-selected",
    },

    apc: { apcLocal: null, apcInternational: null },
  }),

  computed: {
    canSubmit() {
      const uploaded = [...this.manuscript.files].map((f) => f.name);

      return (
        this.manuscript._id &&
        this.manuscript.authors.length > 0 &&
        uploaded.includes("MAIN-MANUSCRIPT") &&
        uploaded.includes("COVER-LETTER")
      );
    },
    selectedSpecialEdition() {
      return this.specialEditions.find(
        (se) => se.id === this.manuscript.edition
      );
    },
  },

  methods: {
    updateManuscript() {
      this.loading = true;

      this.$axios
        .put(`/manuscripts`, {
          ...this.manuscript,
          ...{ abstract: this.cleanReviewText(this.manuscript.abstract) },
        })
        .then(() => {
          this.$bus.$emit("snackbar", {
            type: "success",
            message: "Manuscript update saved",
          });

          this.$bus.$emit("manuscript.refresh");
          this.fetchManuscript();
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not save update",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchManuscript() {
      if (this.manuscriptId) {
        this.$axios
          .get(`/manuscript/${this.manuscriptId}`)
          .then(({ data: response }) => {
            if (response.submissionStatus == "INCOMPLETE") {
              this.manuscript = response;
              this.forSpecialEdition = !!response.edition;
            } else {
              this.$bus.$emit("snackbar", {
                type: "warning",
                message: "Manuscript already submitted",
              });

              this.$router.push({
                path: "/manuscripts/incomplete",
              });
            }
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not load manuscript",
            });
            this.$router.push({
              path: "/manuscripts/incomplete",
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    submitManuscript() {
      if (this.manuscriptId) {
        this.submitting = true;

        this.$axios
          .post(`/manuscript/${this.manuscriptId}/submit`)
          .then(() => {
            this.$bus.$emit("manuscript.refresh");
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Manuscript submitted for review",
            });
            this.$router.push({
              path: "/manuscripts/submitted",
            });
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not submit manuscript",
            });
          })
          .finally(() => {
            this.submitting = false;
          });
      }
    },
    fetchApc() {
      this.$axios.get("/configuration/apc").then(({ data: apc }) => {
        this.apc = apc;
      });
    },
    cleanReviewText(text) {
      return dedent(text.replace(/style="(.*?)" /g, ""));
    },
    fetchSpecialEditions() {
      this.$axios.get("/active-special-editions").then(({ data: response }) => {
        this.specialEditions = response;
      });
    },
  },

  mounted() {
    // let searchParams = new URLSearchParams(window.location.search);

    // if (searchParams.has("manuscript")) {
    //   this.manuscriptId = searchParams.get("manuscript");
    //   this.fetchManuscript();
    // }
    this.fetchSpecialEditions();
    this.fetchManuscript();
    this.fetchApc();
  },
};
</script>
